// packages/components/src/support-ai/MessageOptions.tsx
import React, { useState } from 'react';
import { Card, Button } from '@dopomogai/ui';
import clsx from 'clsx';
import { MessageSquareQuote, Languages } from 'lucide-react';

interface UIMessageOption {
  text: string; // English version
  originalLanguageText: string; // Customer's language version
}

interface MessageOptionsProps {
  options: UIMessageOption[];
  onSelect: (message: UIMessageOption) => void;
  selectedOption?: UIMessageOption | null;
}

// --- NEW Sub-component for a single, stateful reply card ---
const ReplyCard: React.FC<{
  option: UIMessageOption;
  isSelected: boolean;
  onSelect: () => void;
}> = ({ option, isSelected, onSelect }) => {
  const [showOriginal, setShowOriginal] = useState(false);

  const textToShow = showOriginal ? option.originalLanguageText : option.text;

  return (
    <div
      onClick={onSelect}
      className={clsx(
        'group rounded-xl transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-emerald-500',
        isSelected ? 'ring-2 ring-emerald-500' : 'ring-0'
      )}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onSelect(); }}
    >
      <Card noHoverEffect className={clsx('cursor-pointer transition-all duration-200 border-2 h-full', isSelected ? 'border-emerald-500 bg-emerald-50 scale-[1.02]' : 'border-black bg-white group-hover:border-slate-500')}>
        <div className="p-4 flex flex-col justify-between h-full">
          <p className="text-sm text-slate-700 italic">"{textToShow}"</p>
          <div className="flex justify-between items-center mt-4">
            <Button
              icon={<Languages size={14} />}
              text={showOriginal ? "Show EN" : "Show Original"}
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation(); // Prevent card selection
                setShowOriginal(s => !s);
              }}
            />
            <Button text={isSelected ? "Selected" : "Select"} variant={isSelected ? 'primary' : 'secondary'} size="sm" disabled={isSelected} className="pointer-events-none"/>
          </div>
        </div>
      </Card>
    </div>
  );
};


export const MessageOptions: React.FC<MessageOptionsProps> = ({ options, onSelect, selectedOption: controlledSelectedOption }) => {
  const [internalSelected, setInternalSelected] = useState<UIMessageOption | null>(null);
  const isControlled = controlledSelectedOption !== undefined;
  const selectedOption = isControlled ? controlledSelectedOption : internalSelected;

  const handleSelect = (option: UIMessageOption) => {
    if (!isControlled) {
      setInternalSelected(option);
    }
    onSelect(option);
  };

  if (!options || options.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      <h4 className="flex items-center gap-2 text-md font-bold text-slate-800">
        <MessageSquareQuote size={20} />
        Proposed Replies
      </h4>
      <div className="space-y-4">
        {options.map((option, index) => (
          <ReplyCard 
            key={index}
            option={option}
            isSelected={selectedOption?.text === option.text}
            onSelect={() => handleSelect(option)}
          />
        ))}
      </div>
    </div>
  );
};